export interface ProjectTeamMember {
    userId?: string
    name: string
    firstName?: string
    lastName?: string
    pseudo?: string
    link?: string
    role: string
    userAvatar?: string
    userProImage?: string
}

export interface Project {
    slug: string
    title: string
    type: string
    formation: string
    formationSlug?: string
    ctaUrl?: string
    realizationDate?: string | null
    image: string
    verticalImage?: string
    mainCapsule?: string
    backgroundImage?: string
    imageAlt?: string
    tags: string[]
    description?: string
    video?: string
    duration?: string
    promotionName?: string
    team?: Array<ProjectTeamMember>
    teamCount?: number
    gallery?: Array<ProjectGalleryItem>
    links?: Array<{ platform: string, url: string, label?: string }>
    metaTitle?: string
    metaDescription?: string
    keywords?: string
    ogImage?: string
    isAIUsed: boolean
    aiDetails?: string
    displayOrder: number
    year?: string | number
    createdAt?: string
    isPublishedOnWebsite?: boolean
}

export interface ProjectGalleryItem {
    type: string
    url: string
    description?: string
}

export const useProjects = () => {
    const config = useRuntimeConfig()
    const apiBase = config.public.apiBase

    const formationMap: Record<string, string> = {
        'game-design': 'Game Design',
        'game-art': 'Game Art',
        'game-programming': 'Game Programming',
        'sound-design': 'Sound Design',
        'developpeur-web-fullstack': 'Développement'
    }

    const getAllProjects = async (): Promise<Project[]> => {
        try {
            const data = await $fetch<Project[]>(`${apiBase}/oxoweb/projects`)
            return (data || []).map(p => ({
                ...p,
                formation: formationMap[p.formationSlug || ''] || p.formation || p.formationSlug || '',
                tags: p.tags || [],
                year: p.realizationDate ? new Date(p.realizationDate).getFullYear() : (p.promotionName?.match(/\d{4}/)?.[0] || '')
            }))
        } catch (e) {
            console.error('Error fetching projects:', e)
            return []
        }
    }

    const getProjectBySlug = async (slug: string): Promise<Project | undefined> => {
        try {
            const data = await $fetch(`${apiBase}/oxoweb/projects/${slug}`)
            const p = data as any
            if (!p) return undefined
            return {
                ...p,
                formation: formationMap[p.formationSlug] || p.formation || p.formationSlug || '',
                tags: p.tags || [],
                year: p.realizationDate ? new Date(p.realizationDate).getFullYear() : (p.promotionName?.match(/\d{4}/)?.[0] || '')
            }
        } catch (e) {
            console.error(`Error fetching project ${slug}:`, e)
            return undefined
        }
    }

    const getProjectsByFormation = async (formation: string): Promise<Project[]> => {
        const all = await getAllProjects()
        if (formation === 'Tous') return all
        return all.filter(p => p.formation === formation)
    }

    const getFormations = async (): Promise<string[]> => {
        try {
            const data = await $fetch<{ title: string }[]>(`${apiBase}/oxoweb/formations`)
            if (!data) return ['Tous']
            return ['Tous', ...data.map(f => f.title)]
        } catch {
            return ['Tous']
        }
    }

    return {
        getAllProjects,
        getProjectBySlug,
        getProjectsByFormation,
        getFormations
    }
}
